import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import config from './config';
import { MathJaxContext, MathJax } from 'better-react-mathjax';

interface QuestionResult {
  questionId: number;
  questionText: string;
  questionLatex?: string;
  userAnswer: string;
  correctAnswer: string;
  correctAnswerLatex?: string;
  isCorrect: boolean;
  marksObtained: number;
  totalMarks: number;
  explanation: string;
}

interface PracticeTestResult {
  id: number;
  practiceTestId: number;
  testName: string;
  score: number;
  passingScore: number;
  passed: boolean;
  timeSpent: number;
  timeLimit: number;
  attemptDate: string;
  detailedResults: QuestionResult[];
}

export const PracticeTestResults: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [result, setResult] = useState<PracticeTestResult | null>(location.state?.result || null);
  const [loading, setLoading] = useState(!result);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!result) {
      fetchResults();
    }
  }, [attemptId, result]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${config.apiBaseUrl}/api/practiceattempts/${attemptId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch results');
      const data = await response.json();
      setResult(data);
      setError(null);
    } catch (err) {
      setError('Failed to load test results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (questionId: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );

  if (error || !result)
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Results not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );

  const correctAnswers = result.detailedResults.filter(
    (r) => r.isCorrect
  ).length;
  const accuracy = (
    (correctAnswers / result.detailedResults.length) * 100
  ).toFixed(1);

  return (
    <MathJaxContext>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-4xl mx-auto">
        {/* Results Header */}
        <div
          className={`rounded-lg shadow-lg p-8 mb-6 text-white ${
            result.passed
              ? 'bg-gradient-to-r from-green-600 to-emerald-600'
              : 'bg-gradient-to-r from-red-600 to-rose-600'
          }`}
        >
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2">
              {result.passed ? '🎉 Congratulations!' : '📝 Try Again'}
            </h1>
            <p className="text-xl mb-6 opacity-90">
              {result.passed
                ? 'You have successfully completed the test!'
                : 'You need to improve to pass this test.'}
            </p>
            <div className="flex justify-center gap-8">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-3xl font-bold">{result.score.toFixed(1)}%</p>
                <p className="text-sm opacity-90">Your Score</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-3xl font-bold">{result.passingScore}%</p>
                <p className="text-sm opacity-90">Passing Score</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-3xl font-bold">{accuracy}%</p>
                <p className="text-sm opacity-90">Accuracy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Test Details */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 mb-2">Time Spent</p>
            <p className="text-2xl font-bold text-blue-600">
              {Math.floor(result.timeSpent / 60)}m {result.timeSpent % 60}s
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 mb-2">Time Limit</p>
            <p className="text-2xl font-bold text-blue-600">
              {result.timeLimit}m
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 mb-2">Correct Answers</p>
            <p className="text-2xl font-bold text-green-600">
              {correctAnswers}/{result.detailedResults.length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 mb-2">Attempt Date</p>
            <p className="text-lg font-semibold text-gray-800">
              {new Date(result.attemptDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Question Reviews */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Question Review
          </h2>

          <div className="space-y-4">
            {result.detailedResults.map((question, idx) => (
              <div
                key={question.questionId}
                className={`border rounded-lg overflow-hidden ${
                  question.isCorrect
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <button
                  onClick={() => toggleQuestion(question.questionId)}
                  className="w-full p-4 flex items-start gap-4 hover:bg-opacity-75 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <span
                      className={`inline-flex items-center justify-center h-8 w-8 rounded-full font-bold text-white ${
                        question.isCorrect ? 'bg-green-600' : 'bg-red-600'
                      }`}
                    >
                      {question.isCorrect ? '✓' : '✗'}
                    </span>
                  </div>

                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-800">
                      Q{idx + 1}:{' '}
                      {question.questionLatex ? (
                        <div className="inline-block bg-gray-100 px-2 py-1 rounded">
                          <MathJax>{`$$${question.questionLatex}$$`}</MathJax>
                        </div>
                      ) : (
                        question.questionText
                      )}
                    </div>
                    <div className="flex gap-4 mt-2">
                      {question.isCorrect && (
                        <span className="text-sm text-green-700">
                          ✓ Correct
                        </span>
                      )}
                      {!question.isCorrect && (
                        <span className="text-sm text-red-700">
                          ✗ Incorrect
                        </span>
                      )}
                      <span className="text-sm text-gray-600">
                        {question.marksObtained}/{question.totalMarks} marks
                      </span>
                    </div>
                  </div>

                  <div className="text-gray-500">
                    {expandedQuestions.has(question.questionId) ? '▼' : '▶'}
                  </div>
                </button>

                {expandedQuestions.has(question.questionId) && (
                  <div className="px-4 pb-4 border-t pt-4">
                    {question.userAnswer && (
                      <div className="mb-4">
                        <p className="font-semibold text-gray-700 mb-2">
                          Your Answer:
                        </p>
                        <p
                          className={`p-3 rounded ${
                            question.isCorrect
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {question.userAnswer}
                        </p>
                      </div>
                    )}

                    {!question.isCorrect && question.correctAnswer && (
                      <div className="mb-4">
                        <p className="font-semibold text-gray-700 mb-2">
                          Correct Answer:
                        </p>
                        <div className="bg-green-100 text-green-800 p-3 rounded">
                          {question.correctAnswerLatex ? (
                            <MathJax>{`$$${question.correctAnswerLatex}$$`}</MathJax>
                          ) : (
                            question.correctAnswer
                          )}
                        </div>
                      </div>
                    )}

                    {question.explanation && (
                      <div>
                        <p className="font-semibold text-gray-700 mb-2">
                          Explanation:
                        </p>
                        <p className="text-gray-700 bg-blue-50 p-3 rounded">
                          {question.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => navigate('/courses')}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            View More Tests
          </button>
        </div>
      </div>
    </div>
    </MathJaxContext>
  );
};
